import React, {createContext, ReactNode, useCallback, useContext, useEffect, useId, useMemo, useState} from "react"

export type BreadcrumbItem =
    | {label: string; url?: string}
    | {render: ReactNode; key: string}

export interface BreadcrumbEntry {
    depth: number
    items: BreadcrumbItem[]
}

export interface BreadcrumbsContextValue {
    depth: number
    entries: ReadonlyMap<string, BreadcrumbEntry>
    register: (id: string, entry: BreadcrumbEntry) => void
    unregister: (id: string) => void
}

export const BreadcrumbsContext = createContext<BreadcrumbsContextValue | undefined>(undefined)

export function BreadcrumbsProvider({children}: {children: ReactNode}) {
    const [entries, setEntries] = useState<Map<string, BreadcrumbEntry>>(() => new Map())

    const register = useCallback((id: string, entry: BreadcrumbEntry) => {
        setEntries(prev => {
            const existing = prev.get(id)
            if (existing && existing.depth === entry.depth && existing.items === entry.items) return prev
            const next = new Map(prev)
            next.set(id, entry)
            return next
        })
    }, [])

    const unregister = useCallback((id: string) => {
        setEntries(prev => {
            if (!prev.has(id)) return prev
            const next = new Map(prev)
            next.delete(id)
            return next
        })
    }, [])

    const value = useMemo<BreadcrumbsContextValue>(
        () => ({depth: 1, entries, register, unregister}),
        [entries, register, unregister],
    )

    return <BreadcrumbsContext.Provider value={value}>{children}</BreadcrumbsContext.Provider>
}

export function useBreadcrumbsContext(): BreadcrumbsContextValue {
    const ctx = useContext(BreadcrumbsContext)
    if (!ctx) throw new Error("useBreadcrumbsContext must be used inside a BreadcrumbsProvider")
    return ctx
}

export function useBreadcrumbs(items: BreadcrumbItem[] | undefined) {
    const id = useId()
    const {depth, register, unregister} = useBreadcrumbsContext()
    const serialized = serializeItems(items)

    useEffect(() => {
        if (!items || items.length === 0) {
            unregister(id)
            return
        }
        register(id, {depth, items})
        return () => unregister(id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, depth, serialized, register, unregister])
}

function serializeItems(items: BreadcrumbItem[] | undefined): string {
    if (!items) return ""
    return items
        .map(item => ("render" in item ? `R:${item.key}` : `L:${item.label}|${item.url ?? ""}`))
        .join("\x1f")
}
